'use client';

import {
  type DependencyList,
  type RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type UseEqualCardHeightsResult<T extends HTMLElement> = {
  collapsedHeight: number;
  registerCardRef: (index: number) => RefCallback<T>;
  recalculate: () => void;
};

export type UseEqualCardHeightsOptions = {
  enabled?: boolean;
};

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useEqualCardHeights<T extends HTMLElement = HTMLDivElement>(
  cardCount: number,
  dependencies: DependencyList = [],
  options?: UseEqualCardHeightsOptions,
): UseEqualCardHeightsResult<T> {
  const refs = useRef<(T | null)[]>([]);
  const [collapsedHeight, setCollapsedHeight] = useState(0);
  const enabled = options?.enabled ?? true;
  const stableCallbacks = useRef<Map<number, RefCallback<T>>>(new Map());

  const recalculate = useCallback(() => {
    if (!enabled) return;

    const heights = refs.current
      .slice(0, cardCount)
      .filter((element): element is T => Boolean(element))
      .map((element) => element.scrollHeight);

    const nextHeight =
      heights.length === 0 ? 0 : Math.ceil(Math.max(...heights));
    setCollapsedHeight((current) =>
      current === nextHeight ? current : nextHeight,
    );
  }, [cardCount, enabled]);

  const registerCardRef = useCallback((index: number): RefCallback<T> => {
    let callback = stableCallbacks.current.get(index);
    if (!callback) {
      callback = (element) => {
        refs.current[index] = element;
      };
      stableCallbacks.current.set(index, callback);
    }
    return callback;
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) return;

    let frameId = window.requestAnimationFrame(recalculate);

    const handleResize = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(recalculate);
    };

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(handleResize)
        : null;

    if (resizeObserver) {
      refs.current.slice(0, cardCount).forEach((element) => {
        if (element) resizeObserver.observe(element);
      });
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [enabled, cardCount, recalculate, ...dependencies]);

  return { collapsedHeight, registerCardRef, recalculate };
}
